import { useEffect, useRef } from 'react';
import { Button } from './Button';

export function PhotoPicker({
  files,
  onChange,
  max,
}: {
  files: File[];
  onChange: (next: File[]) => void;
  max: number;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  // One object URL per File, created once and reused across re-renders.
  // `files` is a single shared array in the parent screen's state, so it
  // re-renders on every keystroke elsewhere in the form; without this cache
  // we'd mint a fresh blob URL per file on every one of those renders and
  // never revoke any of them.
  const urlCache = useRef<Map<File, string>>(new Map());

  const remaining = max - files.length;

  // Revoke URLs for photos that are no longer selected (e.g. the plumber
  // tapped remove). Runs whenever the file list changes, not only on
  // unmount, so removing a photo mid-session frees it immediately.
  useEffect(() => {
    const cache = urlCache.current;
    for (const file of cache.keys()) {
      if (!files.includes(file)) {
        URL.revokeObjectURL(cache.get(file)!);
        cache.delete(file);
      }
    }
  }, [files]);

  // Revoke whatever is left when the picker unmounts.
  useEffect(() => {
    const cache = urlCache.current;
    return () => {
      for (const url of cache.values()) {
        URL.revokeObjectURL(url);
      }
      cache.clear();
    };
  }, []);

  function urlFor(file: File): string {
    let url = urlCache.current.get(file);
    if (!url) {
      url = URL.createObjectURL(file);
      urlCache.current.set(file, url);
    }
    return url;
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        style={{ display: 'none' }}
        onChange={(event) => {
          const picked = Array.from(event.target.files ?? []);
          onChange([...files, ...picked].slice(0, max));
          event.target.value = '';
        }}
      />
      <Button
        type="button"
        variant="secondary"
        full
        disabled={remaining <= 0}
        onClick={() => inputRef.current?.click()}
      >
        {remaining > 0 ? `Add photos (${remaining} left)` : `Photo limit reached (${max})`}
      </Button>

      {files.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))',
            gap: '0.5rem',
            marginTop: '0.5rem',
          }}
        >
          {files.map((file, index) => (
            <div key={`${file.name}-${index}`} style={{ position: 'relative' }}>
              <img
                src={urlFor(file)}
                alt={`Selected photo ${index + 1}`}
                style={{
                  width: '100%',
                  aspectRatio: '1 / 1',
                  height: 'auto',
                  objectFit: 'cover',
                  borderRadius: 'var(--radius-sm)',
                  display: 'block',
                }}
              />
              <button
                type="button"
                aria-label={`Remove photo ${index + 1}`}
                onClick={() => onChange(files.filter((_, i) => i !== index))}
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: 44,
                  height: 44,
                  padding: 6,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-end',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 28,
                    height: 28,
                    lineHeight: '28px',
                    textAlign: 'center',
                    borderRadius: '50%',
                    background: 'rgba(0,24,56,0.75)',
                    color: '#fff',
                    fontSize: '1rem',
                  }}
                >
                  x
                </span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
